// SPDX-License-Identifier: Elastic-2.0
/**
 * AuthGuard (T-004 / T-007 / F-348).
 *
 * Wraps the authenticated-app sub-tree. Redirects to A5 (account
 * pairing) with `?reauth=true` only when the install has **no identity
 * at all**, per functional spec § 8 and F-348.
 *
 * Two identities authorize the app:
 *   - a cloud JWT (`mode === "cloud"`), or
 *   - an anonymous `device_id` (`mode === "local-only"`, i.e. account-less
 *     Free per ADR-002 Amendment §A.1).
 * Only `unauthenticated` (neither) is bounced to pairing.
 *
 * Reads `mode` from `useAuth` (Zustand store) so the guard re-renders
 * when the session is cleared mid-session (e.g. the orchestrator wipes
 * the cloud session after a failed refresh, flipping mode).
 */
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../api/auth-store";
import { ROUTES } from "./routes";

export function AuthGuard(): JSX.Element {
  const location = useLocation();
  // Account-less (device_id) installs resolve to "local-only" and stay in
  // the app; only a truly identity-less install is redirected to pairing.
  const mode = useAuth((state) => state.mode);

  if (mode === "unauthenticated") {
    const target = `${ROUTES.ONBOARDING_ACCOUNT}?reauth=true`;
    return (
      <Navigate
        to={target}
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return <Outlet />;
}
